import type { ReactNode } from "react";
import { Button } from "./Button";

interface ScreenStateProps {
  loading: boolean;
  error: Error | null;
  onRetry?: (() => void) | undefined;
  /**
   * A way out when something breaks.
   *
   * Error screens without a footer (tab bar) or a back handler are dead ends:
   * the student can only refresh. Pass a callback so the error state always
   * offers a route back into the product.
   */
  onBack?: (() => void) | undefined;
  /**
   * Chrome that must outlive a failure — the tab bar, on the four tab routes.
   *
   * The tab bar lives inside each screen, so an error replaced it along with
   * the content and left the student on a dead end with no way back except the
   * browser's own back button.
   */
  footer?: ReactNode | undefined;
  /**
   * Chrome above the content that must outlive loading — the exam switcher.
   * Without it a switch replaced the switcher with the skeleton, so the tap
   * looked like it had done nothing until the new exam's data arrived.
   */
  header?: ReactNode | undefined;
  /**
   * Replace the generic "We couldn't load that" with a custom error view.
   *
   * When provided and `error` is truthy, this renders instead of the default
   * error card. Useful for domain-specific errors like empty topics that need
   * their own UI (feedback form, etc.).
   */
  renderError?: ((error: Error) => ReactNode) | undefined;
  children: ReactNode;
}

/**
 * Loading and error states, once.
 *
 * A skeleton, never a spinner on a blank screen — the brand rules are explicit,
 * and on a slow connection a spinner tells a student nothing about whether to
 * wait. The error names what failed and offers a retry rather than leaving the
 * screen empty.
 */
export function ScreenState({
  loading,
  error,
  onRetry,
  onBack,
  footer,
  header,
  renderError,
  children,
}: ScreenStateProps) {
  if (loading) {
    return (
      <div className="app-column">
        {header}
        <div className="flex flex-1 flex-col gap-3 px-5 pt-8" role="status" aria-label="Loading">
          {[0, 1, 2].map((row) => (
            <div
              key={row}
              className="h-20 animate-pulse rounded-[var(--radius-card)] border border-border bg-surface"
            />
          ))}
        </div>
        {footer}
      </div>
    );
  }

  if (error) {
    if (renderError) return <>{renderError(error)}</>;

    return (
      <div className="app-column">
        {header}
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-5 text-center">
          <p className="m-0 font-[family-name:var(--font-display)] text-[20px] font-bold">
            We couldn't load that.
          </p>
          <p className="m-0 max-w-[300px] text-[13.5px] leading-[1.5] text-text-muted">
            {error.message}
          </p>
          <div className="flex w-full max-w-[280px] flex-col gap-2 pt-2">
            {onRetry ? <Button onClick={onRetry}>Try again</Button> : null}
            {onBack ? (
              <Button variant="ghost" onClick={onBack}>
                Go back
              </Button>
            ) : null}
          </div>
        </div>
        {footer}
      </div>
    );
  }

  return (
    <>
      {header}
      {children}
    </>
  );
}
